<script setup lang="ts">
import type { Step } from '../types/types'

const stepStyle = {
  badge: {
    activeBackgroundColor: 'primary',
    activeTextColor: 'text-white',
    inactiveBackgroundColor: 'primary/5',
    inactiveTextColor: 'text-primary'
  },
  activeTextColor: 'primary',
  inactiveTextColor: 'gray-600'
}

const props = defineProps<{
  steps: Step[]
  hasStepNumber?: boolean
  currentStep: number
}>()

const emit = defineEmits<(e: 'update:currentStep', currentStep: number) => void>()

const changeStep = (step) => {
  emit('update:currentStep', step)
}

const isCurrentStep = (stepOrder) => {
  return props.currentStep === stepOrder
}

const activeBadgeStyle = computed(
  () => `bg-${stepStyle.badge.activeBackgroundColor} ${stepStyle.badge.activeTextColor}`
)

const inactiveBadgeStyle = computed(
  () =>
    `bg-${stepStyle.badge.inactiveBackgroundColor} ${stepStyle.badge.inactiveTextColor} group-hover:bg-primary group-hover:text-white`
)
</script>
<template>
  <div class="min-w-max">
    <nav aria-label="Progress">
      <ol class="flex flex-col gap-4 md:flex-row md:space-x-8 md:space-y-0">
        <li
          v-for="(step, index) in steps"
          :key="index"
          class="group grow md:flex-1 md:border-t-4"
          :class="[
            isCurrentStep(step.stepOrder)
              ? `border-${stepStyle.badge.activeBackgroundColor}`
              : 'border-border md:hover:border-foreground/20'
          ]"
        >
          <button
            class="flex w-full items-center border-l-4 py-2 pl-4 focus:outline-hidden md:border-l-0 md:pb-0 md:pl-0 md:pt-4"
            @click="changeStep(step.stepOrder)"
          >
            <div v-if="hasStepNumber">
              <span
                v-if="!step.isComplete"
                :class="[isCurrentStep(step.stepOrder) ? activeBadgeStyle : inactiveBadgeStyle]"
                class="mr-3 inline-flex aspect-square w-8 items-center justify-center rounded-full text-sm"
                >{{ step.stepOrder }}</span
              >
              <div
                v-else
                class="mr-3 inline-flex aspect-square w-8 items-center justify-center rounded-full bg-primary text-sm"
              >
                <BaseIcon name="HeroCheckIconOutline" class="aspect-square w-5 text-white" />
              </div>
            </div>
            <div class="flex flex-col text-left">
              <span
                class="text-xs font-semibold uppercase tracking-wide group-hover:text-primary"
                :class="
                  isCurrentStep(step.stepOrder)
                    ? `text-${stepStyle.activeTextColor}`
                    : `text-${stepStyle.inactiveTextColor}`
                "
              >
                {{ step.title }}
              </span>
              <span class="text-xs font-medium text-foreground-secondary">
                {{ step?.subtitle }}
              </span>
            </div>
          </button>
        </li>
      </ol>
    </nav>
  </div>
</template>
